import * as yup from "yup";
import registrationValidationSchema from "../../../utils/validation-schema/registrationValidation";

export const getRegistrationSteps = (invited) => [
  {
    key: "contact",
    fields: [
      "name",
      "email",
      "phoneCode",
      "phoneNumber",
      "password",
      "confirmPassword",
    ],
  },
  ...(!invited
    ? [{ key: "organization", fields: ["orgName", "orgGSTNo", "promoCode"] }]
    : []),
];

export const getRegistrationSchema = (invited) =>
  registrationValidationSchema.shape({
    name: yup.string().trim().required("Enter your full name."),
    email: yup
      .string()
      .trim()
      .email("Enter a valid email address.")
      .required("Enter your email address."),
    phoneCode: yup.string().required("Select a country code."),
    phoneNumber: yup.string().trim().required("Enter your phone number."),
    password:
      registrationValidationSchema.fields.password.required(
        "Create a password."
      ),
    confirmPassword:
      registrationValidationSchema.fields.confirmPassword.required(
        "Confirm your password."
      ),
    orgName: invited
      ? yup.string()
      : yup.string().trim().required("Enter your organization name."),
    orgGSTNo: yup.string(),
    promoCode: yup.string(),
  });

export const readPromoCodeParam = (searchParams) => {
  const raw =
    searchParams.get("promo_code") ?? searchParams.get("promoCode") ?? "";
  return String(raw).trim().toUpperCase();
};

// Keep the API field name in one place; an empty code is not sent.
const promotionPayload = (values, invited) => {
  const code = values.promoCode.trim();
  return !invited && code ? { promo_code: code } : {};
};

export const buildRegistrationPayload = (values, invited) => ({
  name: values.name.trim(),
  email: values.email.trim(),
  phoneCode: values.phoneCode,
  phoneNumber: values.phoneNumber.trim(),
  password: values.password,
  address: "-",
  orgName: invited ? "-" : values.orgName.trim(),
  orgGSTNo: invited ? "-" : values.orgGSTNo.trim(),
  ...promotionPayload(values, invited),
});

// Passwords must never be included in the draft saved for the SSO redirect.
export const buildMicrosoftRegistrationDraft = (values) => ({
  name: values.name.trim(),
  phoneCode: values.phoneNumber.trim() ? values.phoneCode : "",
  phoneNumber: values.phoneNumber.trim(),
  address: "",
  orgName: values.orgName.trim(),
  orgGSTNo: values.orgGSTNo.trim(),
  ...promotionPayload(values, false),
});

export const getRegistrationFeedback = (error, microsoft = false) => {
  const cause = error?.error || error;
  const status = cause?.response?.status || cause?.status;
  const raw = cause?.response?.data?.message || cause?.message;
  const result = (title, message) => ({ severity: "error", title, message });
  if (status === 429)
    return result(
      "Please wait before trying again",
      "Too many requests were made. Wait a few minutes, then try again."
    );
  if (
    ["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"].includes(cause?.code) ||
    raw === "Network Error"
  ) {
    return result(
      "Unable to connect",
      "Check your internet connection and try again. Your entries have been kept."
    );
  }
  if (
    raw === "Email or phone number already in use" ||
    raw === "A user with this phone number already exists."
  ) {
    return {
      ...result(
        "Account details already in use",
        "Use a different email address or phone number, or sign in to your existing account."
      ),
      returnToStep: "contact",
    };
  }
  if (raw === "Account is not eligible for registration") {
    return result(
      "Registration is unavailable for this account",
      "Try signing in, or contact your administrator for help with your invitation."
    );
  }
  if (raw === "This sub-user must complete registration via Microsoft SSO.") {
    return result(
      "Use your Microsoft invitation",
      "Open the Microsoft registration link in your invitation email to continue."
    );
  }
  return result(
    microsoft
      ? "Microsoft registration could not start"
      : "Unable to create your account",
    "Please try again. If the problem continues, contact support. Your entries have been kept."
  );
};
